import React, { useMemo, useState, useEffect } from "react";
import { Grid, Divider } from "@mui/material";
import { DataGridTable, DatePickerField, InputButton, InputTextField } from "components/UI";
import { useHistorySearch } from "shared/services/master/barcodeprint";
import { messageDisplay, responseErrors } from "utils";
import { COMMON_ENUM, FIRST_PAGE } from "shared/constants";
import useColumnHistory from "../hooks/useColumnHistory";

const HistoryMode = ({ printType, setMsgError, setMsgAlert, functionId }) => {
  const [mode, setMode] = useState("view");
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [onSearch, setOnSearch] = useState(false);

  const [form, setForm] = useState({
    printType: printType,
    printDateFrom: "",
    printDateTo: "",
    barcodeId: "",
  });

  // columns
  const columns = useMemo(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useColumnHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  useEffect(() => {
    document.getElementById("input_printDateFrom").focus();
  }, []);

  // api
  const searchDataAsync = useHistorySearch();

  // get search
  const getSearch = async (pageNumber = 1) => {
    try {
      setMsgError([]);
      setMsgAlert([]);
      setOnSearch(true);
      console.log(form);
      const searchData = await searchDataAsync({
        dataOwner: COMMON_ENUM.C_SM_CTG_TMAP_EM,
        printType: form.printType,
        barcodeId: form.barcodeId,
        printDateFrom: form.printDateFrom,
        printDateTo: form.printDateTo,
        pageNumber: pageNumber,
        rowsPerPage: 10,
      });
      console.log("searchData : ", searchData);

      const data =
        searchData?.result?.data?.map((item, index) => ({
          rowNumber: index + 1,
          ...item,
        })) ?? [];

      return { searchData, data };
    } catch (error) {
      const errors = responseErrors(error);
      setMsgError(errors);
      window.scrollTo(0, 0);
      return;
    }
  };

  // handle search
  const handleSearch = async () => {
    try {
      // const { searchData, data } = await getSearch();
      setMsgError([]);
      setMsgAlert([]);
      setRows([]);
      setPageNumber(FIRST_PAGE);
      setPagination({});
      setOnSearch(false);

      const body = {
        dataOwner: COMMON_ENUM.C_SM_CTG_TMAP_EM,
        printType: form.printType,
        barcodeId: form.barcodeId,
        printDateFrom: form.printDateFrom,
        printDateTo: form.printDateTo,
        pageNumber: FIRST_PAGE,
        rowsPerPage: 10,
      };
      const searchData = await searchDataAsync(body);

      const data =
        searchData?.result?.data?.map((item, index) => ({
          rowNumber: index + 1,
          printDt: item.printDt,
          barcodeIdFrom: item.barcodeIdFrom,
          barcodeIdTo: item.barcodeIdTo,
          printBy: item.printBy,
          printQty: item.printQty,
          numOfCopy: item.numOfCopy,
          printType: item.printType,
          ...item,
        })) ?? [];
      if (!data?.length) {
        setMsgError([messageDisplay({ type: "error", code: "MSTD0059AERR", msg: "" })]);
        return;
      }
      setPagination(searchData?.result?.pagination ?? {});
      setPageNumber(searchData?.result?.pagination?.pageNumber);
      setRows(data);
      setOnSearch(true);
    } catch (error) {
      const errors = responseErrors(error);
      setMsgError(errors);
      window.scrollTo(0, 0);
      return;
    }
  };

  // handle clear
  const handleClear = () => {
    setMsgError([]);
    setMsgAlert([]);
    setRows([]);
    setPageNumber(FIRST_PAGE);
    setPagination({});
    setOnSearch(false);

    setForm(old => ({
      ...old,
      printDateFrom: "",
      printDateTo: "",
      barcodeId: "",
    }));
    setTimeout(() => {
      document.getElementById("input_printDateFrom").focus();
    }, 600);
  };

  // 1. pagination
  const [pagination, setPagination] = useState({});
  const [pageNumber, setPageNumber] = useState(1);

  const handleChangePagination = async (event, value) => {
    try {
      setMsgError([]);
      setMsgAlert([]);
      setPageNumber(value);
      const { searchData, data } = await getSearch(value);
      setPagination(searchData?.result?.pagination ?? {});
      setPageNumber(searchData?.result?.pagination?.pageNumber);
      setRows(data);
    } catch (error) {
      const errors = responseErrors(error);
      setMsgError(errors);
      window.scrollTo(0, 0);
      return;
    }
  };
  return (
    <>
      <Grid item xs={7} />
      <Grid item xs={5} style={{ display: "flex" }}>
        <strong style={{ margin: "auto 0px", width: "325px" }}>*Print Date (From) : </strong>
        <DatePickerField
          id="input_printDateFrom"
          fullWidth={true}
          onChange={e =>
            setForm(old => ({
              ...old,
              printDateFrom: e,
            }))
          }
          value={form.printDateFrom}
          required
          tabIndex={1}
        />
      </Grid>
      <Grid item xs={1} />

      <Grid item xs={5} style={{ display: "flex" }}>
        <strong style={{ margin: "auto 0px", width: "325px" }}>Print Date (To) : </strong>
        <DatePickerField
          id="input_printDateTo"
          fullWidth={true}
          onChange={e =>
            setForm(old => ({
              ...old,
              printDateTo: e,
            }))
          }
          value={form.printDateTo}
          tabIndex={1}
        />
      </Grid>
      <Grid item xs={1} />

      <Grid item xs={5} style={{ display: "flex" }}>
        <strong style={{ margin: "auto 0px", width: "325px" }}>R-Module Barcode ID : </strong>
        <InputTextField
          fullWidth
          id="input_barcodeId"
          value={form?.barcodeId}
          onChange={e => {
            setForm(old => ({
              ...old,
              barcodeId: e.target.value,
            }));
          }}
          toUpperCase={true}
          regularExp={/^[a-zA-Z0-9*]*$/}
          maxLength={14}
          tabIndex={1}
        />
      </Grid>
      <Grid item xs={12} display="flex" justifyContent="flex-end" marginTop="10px">
        <InputButton value="Search" onClick={handleSearch} name={`${functionId}Search`} tabIndex={1} />
        <InputButton
          value="Clear"
          onClick={handleClear}
          onKeyDown={e => {
            if (e.key === "Tab") {
              e.preventDefault();
              document.getElementById("input_printDateFrom").focus();
            }
          }}
          name={`${functionId}Clear`}
          tabIndex={1}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>
      <Grid item xs={12}>
        {onSearch && (
          <DataGridTable
            mode={mode}
            onSearch={onSearch}
            setMode={setMode}
            rows={rows}
            checkboxSelection={false}
            rowHeight={35}
            // rowHeightAuto={false}
            rowSelection={{
              rowModesModel: rowModesModel,
              setRowModesModel: setRowModesModel,
              rowSelectionModel: rowSelectionModel,
              setRowSelectionModel: setRowSelectionModel,
            }}
            pagination={{
              pagination: pagination,
              pageNumber: pageNumber,
              handleChangePagination: handleChangePagination,
            }}
            column={{
              columns: columns,
              columnGroupingModel: null,
            }}
            tableHeight="500px"
            // slotProps={{
            //   baseCheckbox: {
            //     tabIndex,
            //   },
            // }}
          />
        )}
        {/* <DataTable
          mode={mode}
          onSearch={onSearch}
          rowSelectionModel={rowSelectionModel}
          setRowSelectionModel={setRowSelectionModel}
          setMode={setMode}
          rows={rows}
          rowModesModel={rowModesModel}
          columns={columns}
          setRowModesModel={setRowModesModel}
          pagination={pagination}
          pageNumber={pageNumber}
          handleChangePagination={handleChangePagination}
          columnGroupingModel={null}
          checkboxSelection={false}
          rowHeight={35}
          rowHeightAuto={false}
          tableHeight="450px"
        /> */}
      </Grid>
    </>
  );
};

export default HistoryMode;
